import { supabase } from './supabaseClient';

export interface Quote {
  id: string;
  quote_number: string;
  quote_date: string;
  client_id: string;
  vendor_id: string | null;
  store_id?: string; // ID du point de vente
  total_ht: number;
  total_tva: number;
  total_ttc: number;
  status: string;
  notes: string | null;
  validity_days: number;
  expiry_date: string | null;
  delivery_info?: any; // JSON avec les informations de livraison
  created_at: string;
  updated_at: string;
  clients?: {
    full_name: string;
    email: string;
    phone: string;
  };
  vendors?: {
    name: string;
  };
}

export interface QuoteItem {
  id: string;
  quote_id: string;
  article_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  tva_rate: number;
  discount_percentage: number;
  total_ht: number;
  total_ttc: number;
  ordre: number;
}

class QuoteService {
  /**
   * Récupérer tous les devis
   */
  async getQuotes(storeFilter?: string) {
    // Tentative avec les relations (si configurées dans Supabase)
    let query = supabase.from('quotes').select(`
        *,
        clients:client_id (
          full_name,
          email,
          phone
        )
      `);

    // Filtre par point de vente si spécifié
    if (storeFilter) {
      query = query.eq('store_id', storeFilter);
    }

    const { data, error } = await query.order('quote_date', { ascending: false });

    // Si erreur 400, les relations ne sont pas configurées - récupérer sans relations
    if (error) {
      let simpleQuery = supabase.from('quotes').select('*');

      // Filtre par point de vente si spécifié
      if (storeFilter) {
        simpleQuery = simpleQuery.eq('store_id', storeFilter);
      }

      const { data: quotesData, error: simpleError } = await simpleQuery.order('quote_date', {
        ascending: false,
      });

      if (simpleError) throw simpleError;

      // Enrichir manuellement avec les données clients et vendors
      const enrichedQuotes = await Promise.all(
        (quotesData || []).map(async quote => {
          const enrichedQuote = { ...quote } as Quote;

          // Récupérer le client
          if (quote.client_id) {
            const { data: client } = await supabase
              .from('clients')
              .select('full_name, email, phone')
              .eq('id', quote.client_id)
              .single();
            if (client) enrichedQuote.clients = client;
          }

          // Récupérer le vendor
          if (quote.vendor_id) {
            const { data: vendor } = await supabase
              .from('vendors')
              .select('name')
              .eq('id', quote.vendor_id)
              .single();
            if (vendor) enrichedQuote.vendors = vendor;
          }

          return enrichedQuote;
        })
      );

      return enrichedQuotes;
    }

    return data as Quote[];
  }

  /**
   * Récupérer un devis par ID
   */
  async getQuoteById(id: string) {
    const { data, error } = await supabase
      .from('quotes')
      .select(
        `
        *,
        clients:client_id (
          full_name,
          email,
          phone,
          address,
          postal_code,
          city,
          country
        )
      `
      )
      .eq('id', id)
      .single();

    if (error) {
      const { data: quoteData, error: simpleError } = await supabase
        .from('quotes')
        .select('*')
        .eq('id', id)
        .single();

      if (simpleError) throw simpleError;

      const enrichedQuote = { ...quoteData } as Quote;

      // Récupérer le client
      if (quoteData.client_id) {
        const { data: client } = await supabase
          .from('clients')
          .select('full_name, email, phone, address, postal_code, city, country')
          .eq('id', quoteData.client_id)
          .single();
        if (client) enrichedQuote.clients = client;
      }

      // Récupérer le vendor
      if (quoteData.vendor_id) {
        const { data: vendor } = await supabase
          .from('vendors')
          .select('name')
          .eq('id', quoteData.vendor_id)
          .single();
        if (vendor) enrichedQuote.vendors = vendor;
      }

      return enrichedQuote;
    }

    return data as Quote;
  }

  /**
   * Créer un nouveau devis
   */
  async createQuote(quoteData: Partial<Quote>) {
    // Générer le numéro de devis avec le préfixe du magasin
    const quoteNumber = await this.generateQuoteNumber(quoteData.store_id);

    const { data, error } = await supabase
      .from('quotes')
      .insert([
        {
          ...quoteData,
          quote_number: quoteNumber,
        },
      ])
      .select()
      .single();

    if (error) throw error;
    return data as Quote;
  }

  /**
   * Mettre à jour un devis
   */
  async updateQuote(id: string, quoteData: Partial<Quote>) {
    const { data, error } = await supabase
      .from('quotes')
      .update(quoteData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Quote;
  }

  /**
   * Supprimer un devis et ses données liées
   */
  async deleteQuote(id: string) {
    try {
      // 1. Supprimer les articles du devis (quote_items)
      const { error: itemsError } = await supabase.from('quote_items').delete().eq('quote_id', id);

      if (itemsError && itemsError.code !== 'PGRST116' && itemsError.code !== '42P01') {
        console.error('Erreur lors de la suppression des articles:', itemsError);
        throw itemsError;
      }

      // 2. Supprimer le devis principal
      const { error: quoteError } = await supabase.from('quotes').delete().eq('id', id);

      if (quoteError) {
        console.error('Erreur lors de la suppression du devis:', quoteError);
        throw quoteError;
      }

      return true;
    } catch (error) {
      console.error('❌ Erreur dans deleteQuote:', error);
      throw error;
    }
  }

  /**
   * Générer un numéro de devis unique avec préfixe du magasin
   * Format: Préfixe + Année (2 chiffres) + ID (5 chiffres)
   * Exemple: DVJ2500001 (DVJ = préfixe Jemeppe, 25 = année 2025, 00001 = ID)
   */
  async generateQuoteNumber(storeId?: string): Promise<string> {
    const today = new Date();
    const year = String(today.getFullYear()).slice(-2); // 2 derniers chiffres de l'année

    // Récupérer le préfixe du magasin
    let prefix = 'DV'; // Préfixe par défaut si pas de magasin

    if (storeId) {
      const { data: storeData, error: storeError } = await supabase
        .from('stores')
        .select('quote_prefix')
        .eq('id', storeId)
        .single();

      if (!storeError && storeData?.quote_prefix) {
        prefix = storeData.quote_prefix;
      }
    }

    // Récupérer le dernier ID pour ce préfixe et cette année
    const pattern = `${prefix}${year}%`;
    const { data, error } = await supabase
      .from('quotes')
      .select('quote_number')
      .like('quote_number', pattern)
      .order('quote_number', { ascending: false })
      .limit(1);

    if (error) throw error;

    let nextId = 1;
    if (data && data.length > 0) {
      const lastNumber = data[0].quote_number;
      // Extraire les 5 derniers chiffres (l'ID)
      const lastId = parseInt(lastNumber.slice(-5));
      nextId = lastId + 1;
    }

    // Format: Préfixe + Année (2 chiffres) + ID (5 chiffres)
    return `${prefix}${year}${String(nextId).padStart(5, '0')}`;
  }

  /**
   * Récupérer les articles d'un devis
   */
  async getQuoteItems(quoteId: string) {
    const { data, error } = await supabase
      .from('quote_items')
      .select(
        `
        *,
        articles (
          name,
          reference
        )
      `
      )
      .eq('quote_id', quoteId)
      .order('ordre', { ascending: true });

    if (error) throw error;
    return data as QuoteItem[];
  }

  /**
   * Ajouter un article à un devis
   */
  async addQuoteItem(itemData: Partial<QuoteItem>) {
    const { data, error } = await supabase.from('quote_items').insert([itemData]).select().single();

    if (error) throw error;
    return data as QuoteItem;
  }

  /**
   * Mettre à jour un article d'un devis
   */
  async updateQuoteItem(id: string, itemData: Partial<QuoteItem>) {
    const { data, error } = await supabase
      .from('quote_items')
      .update(itemData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as QuoteItem;
  }

  /**
   * Supprimer un article d'un devis
   */
  async deleteQuoteItem(id: string) {
    const { error } = await supabase.from('quote_items').delete().eq('id', id);

    if (error) throw error;
  }

  /**
   * Supprimer tous les articles d'un devis
   */
  async deleteAllQuoteItems(quoteId: string) {
    const { error } = await supabase.from('quote_items').delete().eq('quote_id', quoteId);

    if (error) throw error;
  }

  /**
   * Recalculer et mettre à jour les totaux d'un devis
   */
  async updateQuoteTotals(quoteId: string) {
    // Récupérer tous les articles du devis
    const items = await this.getQuoteItems(quoteId);

    // Calculer les totaux
    let totalHT = 0;
    let totalTTC = 0;

    items.forEach(item => {
      totalHT += item.total_ht || 0;
      totalTTC += item.total_ttc || 0;
    });

    const totalTVA = totalTTC - totalHT;

    // Mettre à jour le devis avec les nouveaux totaux
    await this.updateQuote(quoteId, {
      total_ht: totalHT,
      total_tva: totalTVA,
      total_ttc: totalTTC,
    });

    return { totalHT, totalTVA, totalTTC };
  }
}

export const quoteService = new QuoteService();
